/*
 Copyright (c), CKSource Holding sp. z o.o. All rights reserved.
 */

import { Collection } from 'ckeditor5/src/utils.js';

import User from './../users/user';
import WebSocketGateway, { IWebSocketGateway, WebSocketGatewayState } from './../websocketgateway/websocketgateway';
import SessionsConnectResponse from './responses/sessionsconnectresponse';
import SessionsConnectMessage from './messages/sessionsconnectmessage';
import SocketConnectMessage from './messages/socketconnectmessage';
import SocketDisconnectMessage from './messages/socketdisconnectmessage';
import Channel from '../websocketgateway/channel';

import MessagesCompressor from './../messagescompressor';

// eslint-disable-next-line @typescript-eslint/naming-convention
export const _SERVICE: number = 3;

type Handler = ( data: Buffer ) => void;

export interface ISocket {
	id: string;

	user?: User;

	userId?: string;

	role?: string;

	permissions?: string[];
}

interface ISocketEvent {
	eventName: string;

	data: Buffer;
}

/**
 * Collection of connected socket to given service.
 */
// eslint-disable-next-line new-cap
class SessionCollection extends Collection<ISocket> {
	private readonly _handlers: Map<string, Handler> = new Map();

	private _channel?: Channel;

	private _wsGateway?: IWebSocketGateway;

	private _connected: boolean;

	private _eventsQueue: ISocketEvent[] = [];

	private _isRunning: boolean = false;

	public constructor( private readonly _id: string, private readonly _sessionType: number ) {
		super( { idProperty: 'id' } );
	}

	/**
	 * Connects SessionCollection to CKEditor Cloud Services..
	 */
	public async connect( wsGateway: IWebSocketGateway ): Promise<void> {
		this._wsGateway = wsGateway;
		this.stopListening( wsGateway, 'change:state' );

		const message: SessionsConnectMessage = new SessionsConnectMessage( this._id, this._sessionType );

		let sessionsConnectResponse: SessionsConnectResponse;

		try {
			const response: Buffer = await this._wsGateway._sendRequest(
				_SERVICE,
				SessionsConnectMessage.TYPE,
				MessagesCompressor.encode( message )
			);

			sessionsConnectResponse = MessagesCompressor.decode( response, SessionsConnectResponse ) as SessionsConnectResponse;
		} catch ( error ) {
			sessionsConnectResponse = new SessionsConnectResponse( this._id, [] );
		}

		this._connectToChannel( this._wsGateway, sessionsConnectResponse.channel, this._sessionType );

		const sessions: ISocket[] = await _loadSocketsData( this._wsGateway, sessionsConnectResponse.sockets );

		for ( const session of sessions ) {
			super.add( session );
		}

		this._connected = true;
		this.fire( 'connected' );

		this.listenTo(
			this._wsGateway,
			'change:state',
			// eslint-disable-next-line @typescript-eslint/naming-convention
			( _0, _1, value ) => this._onWsGatewayStateChange( value as WebSocketGatewayState ),
			{ priority: WebSocketGateway._CHANGE_STATE_EVENT_PRIORITY }
		);

		await this._runQueue();
	}

	/**
	 * Disconnects SessionCollection from CKEditor Cloud Services.
	 */
	public disconnect( force: boolean = true ): void {
		if ( !this._connected ) {
			return;
		}

		this._connected = false;
		this._eventsQueue = [];

		while ( this.length ) {
			super.remove( 0 );
		}

		if ( this._channel ) {
			this.stopListening( this._channel );

			this._channel = undefined;
		}

		if ( this._wsGateway && force ) {
			this.stopListening( this._wsGateway );

			this._wsGateway = undefined;
		}

		this.fire( 'disconnected' );

		if ( force ) {
			this.stopListening();
		}
	}

	// Make this collection read only
	// eslint-disable-next-line @typescript-eslint/naming-convention
	public add( _1: ISocket, _2?: number ): this {
		throw new TypeError( 'The collection is read-only.' );
	}

	// Make this collection read only
	// eslint-disable-next-line @typescript-eslint/naming-convention
	public remove( _: number ): ISocket {
		throw new TypeError( 'The collection is read-only.' );
	}

	private _connectToChannel( wsGateway: IWebSocketGateway, channelName: string, sessionType: number ): void {
		this._channel = wsGateway._getChannel( sessionType, channelName );

		if ( !this._channel ) {
			return;
		}

		this._addHandler( this._channel, SocketConnectMessage.TYPE, async data => {
			const socketConnectMessage: SocketConnectMessage = MessagesCompressor.decode(
				data,
				SocketConnectMessage
			) as SocketConnectMessage;

			if ( this.getIndex( socketConnectMessage.id ) === -1 ) {
				const socket: ISocket = {
					id: socketConnectMessage.id,
					role: socketConnectMessage.role,
					permissions: socketConnectMessage.permissions
				};

				if ( socketConnectMessage.userId ) {
					socket.user = await User.get( wsGateway, socketConnectMessage.userId );
				}

				super.add( socket );
			}
		} );

		this._addHandler( this._channel, SocketDisconnectMessage.TYPE, data => {
			const socketDisconnectMessage: SocketDisconnectMessage = MessagesCompressor.decode(
				data,
				SocketDisconnectMessage
			) as SocketDisconnectMessage;

			if ( this.getIndex( socketDisconnectMessage.id ) !== -1 ) {
				super.remove( socketDisconnectMessage.id );
			}
		} );
	}

	private async _onWsGatewayStateChange( state: WebSocketGatewayState ): Promise<void> {
		if ( state === WebSocketGateway.STATE_DISCONNECTED ) {
			this.disconnect( false );
		}

		if ( state === WebSocketGateway.STATE_CONNECTED ) {
			await this.connect( this._wsGateway! );
		}
	}

	private async _runQueue(): Promise<void> {
		if ( this._isRunning || !this._connected ) {
			return;
		}

		this._isRunning = true;

		let event: ISocketEvent | undefined;

		while ( ( event = this._eventsQueue.shift() ) ) {
			const handler: Handler | undefined = this._handlers.get( event.eventName );

			if ( handler ) {
				await handler( event.data );
			}
		}

		this._isRunning = false;
	}

	private _addHandler( channel: Channel, eventType: string, handler: Handler ): void {
		const eventName: string = channel.getEventName( eventType, true );

		this.listenTo(
			channel,
			eventName,
			async ( event, data ) => {
				const name: string = event.name;

				this._eventsQueue.push( { eventName: name, data } );

				await this._runQueue();
			}
		);

		this._handlers.set( eventName, handler );
	}
}

export default SessionCollection;

async function _loadSocketsData( wsGateway: IWebSocketGateway, session: ISocket[] ): Promise<ISocket[]> {
	const userIds: string[] = session.map( socket => socket.userId! );

	const users: User[] = userIds.length ? await User.getMany( wsGateway, userIds ) : [];

	return session.map( socket => {
		const item: ISocket = {
			id: socket.id,
			role: socket.role,
			permissions: socket.permissions
		};

		// The first part of the expression can return empty string
		// eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
		item.user = ( socket.userId && users.find( user => user.id === socket.userId ) ) || new User();

		return item;
	} );
}
